/* eslint-disable react/jsx-props-no-spreading */
import AddIcon from "@mui/icons-material/Add"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteIcon from "@mui/icons-material/Delete"
import { Collapse, Divider, Stack } from "@mui/material"
import { ReactElement, useContext, useEffect } from "react"
import { FormProvider, useFieldArray } from "react-hook-form"
import { TransitionGroup } from "react-transition-group"

import { useGetUserData } from "../../../../../hooks/useGetUserData"
import useSubmitDraft from "../../../../../services/middleware/hooks/draftApplication/useSubmitDraft"
import { IFormData } from "../../../../../services/middleware/types/amendApplication/launchAmendmentTypes"
import { ISingleArrayFormSpec } from "../../../../../services/middleware/types/singleApplication/getSchemaTypes"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import {
    prepareFormProperties,
    processUserAccountDerivedInputs,
} from "../../../../../utils/submitApplication/submitApplicationUtils"
import AppButton from "../../../../buttons/appButton"
import ArrayFormErrorDisplay from "../../alerts/arrayFormErrorDisplay"
import ArrayFormGlobalErrorDisplay from "../../alerts/arrayFormGlobalErrorDisplay"
import { ApplicationContext } from "../../applicationProvider"
import InputGenerator from "../../inputGenerator/inputGenerator"
import {
    DERIVED_INPUT,
    FIXED_INPUT,
    USER_ACCOUNT_DERIVED_INPUT,
} from "../../recognisedInputs/recognisedInputKeys"
import BackNextButtons from "../backNextButtons"
import { useFormGenerator } from "../useFormGenerator"

interface SingleArrayFormGeneratorProps {
    formKey: string
    formProperties: ISingleArrayFormSpec
}

/**
 * Generates a single array form. A single array form is a form that is
 * dynamically generated, but the output is an array of objects. Each object
 * is a fixed set of key-value pairs
 */
const SingleArrayFormGenerator = ({
    formKey,
    formProperties,
}: SingleArrayFormGeneratorProps): ReactElement => {
    const {
        setFormData,
        setCurrentStepIndex,
        formData,
        isFirstStep,
        onClickBackFirstStep,
    } = useContext(ApplicationContext)
    const { postDraftApplication } = useSubmitDraft()
    const initialValuesArray = formData?.[formKey] as { [x: string]: object }[]
    const { email, firstName, lastName } = useGetUserData()

    const newDevice = Object.entries(formProperties.items.properties).reduce(
        (acc, [key]) => ({
            ...acc,
            [key]: undefined,
        }),
        {}
    )

    const initialValues = {
        [formKey]: initialValuesArray ?? [newDevice],
    }
    const { errors, ...formMethods } =
        useFormGenerator<typeof initialValues>(initialValues)

    const { fields, append, remove } = useFieldArray({
        control: formMethods.control,
        name: formKey,
    })

    // Track changes to the form data and update context
    const watchedValues = formMethods.watch()
    useEffect(() => {
        setFormData(
            (prev) =>
                ({
                    ...prev,
                    [formKey]: watchedValues[formKey],
                }) as unknown as IFormData
        )
    }, [watchedValues, formKey, setFormData])

    /** Called when user clicks next and the form is valid validation */
    const onClickNext = async (): Promise<void> => {
        const formWatch = JSON.parse(JSON.stringify(formMethods.getValues()))

        // Process form values
        const formValues = Object.entries(
            formProperties.items.properties
        ).reduce<{ [x: string]: unknown | undefined }[]>(
            (acc, [key, value]) => {
                const inputType = value.json_schema_extra.type

                if (inputType === FIXED_INPUT) {
                    return acc.map((item) => ({
                        ...item,
                        [key]: value.json_schema_extra.fixedValue,
                    }))
                }

                if (inputType === USER_ACCOUNT_DERIVED_INPUT) {
                    return processUserAccountDerivedInputs(
                        acc,
                        key,
                        value,
                        email,
                        firstName,
                        lastName
                    )
                }

                if (inputType === DERIVED_INPUT) {
                    return acc.map((item) => {
                        const sourceValue =
                            item[
                                value.json_schema_extra.derivedValue?.source ??
                                    ""
                            ]
                        return {
                            ...item,
                            [key]:
                                value.json_schema_extra.derivedValue
                                    ?.sourceOutputMappings[
                                    sourceValue as string
                                ] ?? null,
                        }
                    })
                }
                return acc
            },
            formWatch[formKey]
        )

        // Update form data in context
        setFormData(
            (prev) => ({ ...prev, ...formValues }) as unknown as IFormData
        )

        postDraftApplication(true)

        // Navigate to next step
        setCurrentStepIndex((prev) => prev + 1)
    }

    /** Duplicates an entry with data carried through */
    const duplicateField = (index: number): void => {
        const fieldValues = formMethods.getValues()[formKey][index]
        append(fieldValues)
    }

    const isOneEntry = fields.length < 2

    return (
        <FormProvider {...formMethods}>
            <>
                <TransitionGroup>
                    {fields.map((field, index) => {
                        const fieldErrors = errors[formKey]?.[index] ?? {}
                        return (
                            <Collapse key={field.id}>
                                <Stack
                                    spacing={GRID_SPACING * 1.5}
                                    marginTop={2}
                                    marginBottom={3}
                                >
                                    {prepareFormProperties(
                                        formProperties.items.properties
                                    ).map(([key, value]) => {
                                        const formKeyIndex = `${formKey}.${index}`
                                        return (
                                            <InputGenerator<object>
                                                key={key}
                                                isAmendment={false}
                                                registerName={`${formKeyIndex}.${key}`}
                                                inputType={
                                                    value.json_schema_extra.type
                                                }
                                                inputSpec={value}
                                                watch={(k) =>
                                                    formMethods.watch(
                                                        `${formKeyIndex}.${k}`
                                                    )
                                                }
                                                programmaticallySetForm={(
                                                    k,
                                                    v
                                                ) =>
                                                    formMethods.setValue(
                                                        `${formKey}.${index}.${k}`,
                                                        v
                                                    )
                                                }
                                            />
                                        )
                                    })}

                                    <Stack
                                        direction="row"
                                        spacing={GRID_SPACING}
                                    >
                                        <AppButton
                                            onClick={() =>
                                                duplicateField(index)
                                            }
                                            variant="outlined"
                                            endIcon={<ContentCopyIcon />}
                                        >
                                            Duplicate
                                        </AppButton>
                                        <Collapse
                                            in={!isOneEntry}
                                            orientation="horizontal"
                                        >
                                            <AppButton
                                                onClick={() => remove(index)}
                                                variant="outlined"
                                                endIcon={<DeleteIcon />}
                                                disabled={isOneEntry}
                                            >
                                                Remove
                                            </AppButton>
                                        </Collapse>
                                    </Stack>
                                    <ArrayFormErrorDisplay
                                        errors={fieldErrors}
                                        formProperties={
                                            formProperties.items.properties
                                        }
                                    />
                                    <Divider />
                                </Stack>
                            </Collapse>
                        )
                    })}
                </TransitionGroup>
                <AppButton
                    onClick={() => append(newDevice)}
                    endIcon={<AddIcon />}
                    variant="outlined"
                >
                    Add New Device
                </AppButton>
            </>

            <ArrayFormGlobalErrorDisplay formErrors={errors[formKey]} />
            <BackNextButtons
                onClickNext={() =>
                    // This will call onClickNext only if validation passes
                    formMethods.handleSubmit(onClickNext)()
                }
                setCurrentStepIndex={setCurrentStepIndex}
                isFirstStep={isFirstStep}
                onClickBackFirstStep={onClickBackFirstStep}
            />
        </FormProvider>
    )
}

export default SingleArrayFormGenerator
